import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bug, Loader2, MessageCircle } from 'lucide-react';
import { feedbackAPI } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const MODULE_OPTIONS = [
  'POS', 'SERVICES', 'INVENTORY', 'PRINT', 'TEMPLATES', 'PURCHASES', 'GAMING', 'AR', 'SETTINGS', 'OTHER'
];

const SEVERITY_OPTIONS = ['low', 'medium', 'high', 'critical'];

const MAX_FILE_BYTES = 500 * 1024;
const FEEDBACK_STATS_REFRESH_MS = 60000;

let feedbackStatsCache = {
  totalOpen: 0,
  fetchedAt: 0,
  pending: null,
};

export default function FeedbackQuickModal({ compact = false, onCreated }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [form, setForm] = useState({
    module: 'POS',
    severity: 'medium',
    title: '',
    description: '',
    screenshot: null,
  });

  const fetchStats = async (force = false) => {
    const now = Date.now();
    if (!force && feedbackStatsCache.fetchedAt && (now - feedbackStatsCache.fetchedAt) < FEEDBACK_STATS_REFRESH_MS) {
      setOpenCount(Number(feedbackStatsCache.totalOpen || 0));
      return;
    }

    if (feedbackStatsCache.pending) {
      try {
        const totalOpen = await feedbackStatsCache.pending;
        setOpenCount(Number(totalOpen || 0));
      } catch {
        setOpenCount(0);
      }
      return;
    }

    feedbackStatsCache.pending = (async () => {
      try {
        const response = await feedbackAPI.getStats();
        const stats = response?.data || {};
        const totalOpen = Number(stats.total_open || 0);
        feedbackStatsCache = {
          totalOpen,
          fetchedAt: Date.now(),
          pending: null,
        };
        return totalOpen;
      } catch (error) {
        feedbackStatsCache = {
          totalOpen: 0,
          fetchedAt: Date.now(),
          pending: null,
        };
        throw error;
      }
    })();

    try {
      const totalOpen = await feedbackStatsCache.pending;
      setOpenCount(Number(totalOpen || 0));
    } catch {
      setOpenCount(0);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    fetchStats();

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchStats();
      }
    }, FEEDBACK_STATS_REFRESH_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchStats();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id]);

  const resetForm = () => {
    setForm({
      module: 'POS',
      severity: 'medium',
      title: '',
      description: '',
      screenshot: null,
    });
  };

  const handleScreenshotChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('Formato inválido. Use PNG, JPG ou WEBP');
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      toast.error('Screenshot excede 500KB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const base64Part = dataUrl.includes(',') ? dataUrl.split(',')[1] : '';
      setForm((prev) => ({
        ...prev,
        screenshot: {
          content_type: file.type,
          filename: file.name,
          size_bytes: file.size,
          data_base64: base64Part,
          preview_url: dataUrl,
        },
      }));
    };
    reader.readAsDataURL(file);
  };

  const submitFeedback = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Preencha título e descrição');
      return;
    }

    const payload = {
      module: form.module,
      severity: form.severity,
      title: form.title.trim(),
      description: form.description.trim(),
      screenshot: form.screenshot
        ? {
            content_type: form.screenshot.content_type,
            filename: form.screenshot.filename,
            size_bytes: form.screenshot.size_bytes,
            data_base64: form.screenshot.data_base64,
          }
        : null,
      email_when_enabled: true,
    };

    try {
      setSubmitting(true);
      const response = await feedbackAPI.createTicket(payload);
      const ticket = response.data;
      toast.success(`Feedback registado: ${ticket.ticket_number}`, {
        action: {
          label: 'Abrir ticket',
          onClick: () => navigate(`/feedback?ticket_id=${ticket.id}`),
        },
      });
      setOpen(false);
      resetForm();
      fetchStats(true);
      if (onCreated) onCreated(ticket);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao criar feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant={compact ? 'ghost' : 'outline'}
        size={compact ? 'icon' : 'sm'}
        onClick={() => setOpen(true)}
        className={compact ? 'text-white hover:bg-white/10 relative' : 'relative'}
        data-testid="quick-feedback-open-button"
      >
        <Bug className="h-4 w-4" />
        {!compact && <span className="ml-2">Feedback</span>}
        {openCount > 0 && (
          <Badge
            className="absolute -top-2 -right-2 bg-red-500 text-white border-red-600 h-5 min-w-[20px] px-1 text-[10px]"
            data-testid="quick-feedback-open-count-badge"
          >
            {openCount}
          </Badge>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="quick-feedback-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Registar Feedback
            </DialogTitle>
            <DialogDescription>
              Reporte o problema em menos de 1 minuto.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label>Módulo</Label>
              <Select value={form.module} onValueChange={(v) => setForm((p) => ({ ...p, module: v }))}>
                <SelectTrigger data-testid="feedback-module-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODULE_OPTIONS.map(option => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Severidade</Label>
              <Select value={form.severity} onValueChange={(v) => setForm((p) => ({ ...p, severity: v }))}>
                <SelectTrigger data-testid="feedback-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEVERITY_OPTIONS.map(option => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label>Título</Label>
              <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="feedback-title-input" />
            </div>

            <div className="col-span-2">
              <Label>Descrição</Label>
              <Textarea rows={5} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} data-testid="feedback-description-input" />
            </div>

            <div className="col-span-2">
              <Label>Screenshot (opcional, máx. 500KB)</Label>
              <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleScreenshotChange} data-testid="feedback-screenshot-input" />
              {form.screenshot && (
                <div className="mt-2 flex items-center gap-3" data-testid="feedback-screenshot-preview-wrapper">
                  <img src={form.screenshot.preview_url} alt="preview" className="h-16 w-24 object-cover rounded border" data-testid="feedback-screenshot-preview" />
                  <div className="text-xs text-muted-foreground">
                    <p>{form.screenshot.filename}</p>
                    <p>{Math.round(form.screenshot.size_bytes / 1024)} KB</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="feedback-cancel-button">Cancelar</Button>
            <Button onClick={submitFeedback} disabled={submitting} data-testid="feedback-submit-button">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Registar Feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
